import type { Envelope, Problem } from '@dashboard/contract';

export class ApiError extends Error {
  constructor(
    readonly problem: Problem,
    readonly status: number,
  ) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
  }
}

export const CSRF_HEADERS = { 'x-requested-with': 'dashboard' } as const;

function loginRedirect(): never {
  const returnTo = `${window.location.pathname}${window.location.search}`;
  window.location.assign(`/auth/login?returnTo=${encodeURIComponent(returnTo)}`);
  throw new ApiError(
    {
      type: 'urn:dashboard:problem:unauthenticated',
      title: 'Sign-in required',
      status: 401,
      code: 'unauthenticated',
    },
    401,
  );
}

async function handle<T>(res: Response): Promise<T> {
  if (res.status === 401) loginRedirect();
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (!res.ok) {
    let problem: Problem;
    try {
      problem = JSON.parse(text) as Problem;
    } catch {
      problem = {
        type: 'about:blank',
        title: res.statusText || 'Request failed',
        status: res.status,
        code: 'internal',
      };
    }
    throw new ApiError(problem, res.status);
  }
  return JSON.parse(text) as T;
}

export async function getJson<T>(path: string, signal?: AbortSignal): Promise<Envelope<T>> {
  const init: RequestInit = { headers: { accept: 'application/json' }, credentials: 'same-origin' };
  if (signal) init.signal = signal;
  return handle<Envelope<T>>(await fetch(path, init));
}

export async function sendJson<T>(
  method: 'POST' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const init: RequestInit = {
    method,
    headers: { ...CSRF_HEADERS, accept: 'application/json', 'content-type': 'application/json' },
    credentials: 'same-origin',
  };
  if (body !== undefined) init.body = JSON.stringify(body);
  return handle<T>(await fetch(path, init));
}

export async function getText(path: string): Promise<string> {
  const res = await fetch(path, { credentials: 'same-origin' });
  if (res.status === 401) loginRedirect();
  if (!res.ok)
    throw new ApiError(
      { type: 'about:blank', title: res.statusText, status: res.status, code: 'internal' },
      res.status,
    );
  return res.text();
}

export function query(params: Record<string, string | number | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') q.set(k, String(v));
  const s = q.toString();
  return s ? `?${s}` : '';
}
